import {useState, useCallback, useEffect} from 'react';
import {RequestService} from 'services/request.service';
import {Prescription} from 'types';
import toast from 'react-hot-toast';

interface UseInlineEditProps {
  product: Prescription;
  fieldName: 'quantity' | 'total_amount' | 'cash_price' | 'payable_cash_price';
  refetch: () => void;
}

interface OptimisticValues {
  quantity?: number;
  cash_price?: number;
  total_amount?: number;
  amount_due?: number;
  payable_cash_price?: number;
}

export const useInlineEdit = ({
  product,
  fieldName,
  refetch,
}: UseInlineEditProps) => {
  const initialValue =
    fieldName === 'quantity'
      ? product?.quantity || 0
      : fieldName === 'cash_price'
      ? product?.cash_price || 0
      : fieldName === 'payable_cash_price'
      ? product?.payable_cash_price || 0
      : product?.total_amount || 0;

  const [value, setValue] = useState<string>(initialValue.toString());
  const [originalValue, setOriginalValue] = useState<string>(
    initialValue.toString()
  );
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [optimisticValues, setOptimisticValues] =
    useState<OptimisticValues | null>(null);

  useEffect(() => {
    setValue(initialValue.toString());
    setOriginalValue(initialValue.toString());
    setOptimisticValues(null);
  }, [initialValue]);

  const validateValue = (val: string): number => {
    const trimmed = val.trim();
    if (trimmed === '') return 0;
    const num = parseFloat(trimmed);
    return isNaN(num) || num < 0 ? 0 : num;
  };

  const saveValue = useCallback(
    async (newValue: string) => {
      const validatedValue = validateValue(newValue);
      const requestService = new RequestService();

      // Convert paid from string to number for calculations
      const paidAmount = parseFloat(product.paid) || 0;
      const payablePaidAmount = parseFloat(product.payable_paid!) || 0;

      let calculatedValues: OptimisticValues;
      let dataToSend: {
        quantity?: number;
        cash_price?: number;
        amount_due?: string;
        payable_cash_price?: number;
        payable_amount_due?: string;
      } = {};

      // Three calculation scenarios:
      if (fieldName === 'cash_price') {
        // Scenario 1: cash_price changed
        const newCashPrice = validatedValue;
        const quantity = product.quantity;
        const totalAmount = quantity * newCashPrice;
        const amountDue = totalAmount - paidAmount;

        // Validate: total_amount cannot be less than paid amount
        if (totalAmount < paidAmount) {
          toast.error(
            `Total amount ($${totalAmount.toFixed(
              2
            )}) cannot be less than paid amount ($${paidAmount.toFixed(2)})`
          );
          setValue(originalValue);
          setError('Total amount cannot be less than paid amount');
          setTimeout(() => setError(null), 5000);
          return;
        }

        calculatedValues = {
          quantity,
          cash_price: newCashPrice,
          total_amount: totalAmount,
          amount_due: amountDue,
        };

        dataToSend = {
          cash_price: newCashPrice,
          amount_due: amountDue.toFixed(2),
        };
      } else if (fieldName === 'quantity') {
        // Scenario 2: quantity changed
        const newQuantity = validatedValue;
        const cashPrice = product.cash_price || 0;
        const totalAmount = newQuantity * cashPrice;
        const amountDue = totalAmount - paidAmount;

        const payableCashPrice = product.payable_cash_price || 0;
        const payableTotalAmount = newQuantity * payableCashPrice;
        const payableAmountDue = payableTotalAmount - payablePaidAmount;
        // Validate: total_amount cannot be less than paid amount
        if (totalAmount < paidAmount) {
          toast.error(
            `Total amount ($${totalAmount.toFixed(
              2
            )}) cannot be less than paid amount ($${paidAmount.toFixed(2)})`
          );
          setValue(originalValue);
          setError('Total amount cannot be less than paid amount');
          setTimeout(() => setError(null), 5000);
          return;
        }

        calculatedValues = {
          quantity: newQuantity,
          cash_price: cashPrice,
          total_amount: totalAmount,
          amount_due: amountDue,
        };

        dataToSend = {
          quantity: newQuantity,
          amount_due: amountDue.toFixed(2),
          payable_cash_price: payableCashPrice,
          payable_amount_due: payableAmountDue.toFixed(2),
        };
      } else if (fieldName === 'payable_cash_price') {
        // Scenario 1: cash_price changed
        const newPayableCashPrice = validatedValue;
        const quantity = product.quantity;
        const totalAmount = quantity * newPayableCashPrice;
        const payableAmountDue = totalAmount - payablePaidAmount;

        // Validate: total_amount cannot be less than paid amount
        //  if (totalAmount < payableAmountDue) {
        //    toast.error(
        //      `Total amount ($${totalAmount.toFixed(
        //        2
        //      )}) cannot be less than paid amount ($${paidAmount.toFixed(2)})`
        //    );
        //    setValue(originalValue);
        //    setError('Total amount cannot be less than paid amount');
        //    setTimeout(() => setError(null), 5000);
        //    return;
        //  }

        calculatedValues = {
          //  quantity,
          //  cash_price: product?.cash_price || 0,
          //  total_amount: product?.total_amount || 0,
          payable_cash_price: newPayableCashPrice,
          //  amount_due: payableAmountDue.toFixed(2),
        };

        dataToSend = {
          payable_cash_price: newPayableCashPrice,
          payable_amount_due: payableAmountDue.toFixed(2),
        };
      } else {
        // Scenario 3: total_amount changed
        const newTotalAmount = validatedValue;
        const quantity = product.quantity;
        // Handle division by zero
        const newCashPrice = quantity > 0 ? newTotalAmount / quantity : 0;
        const amountDue = newTotalAmount - paidAmount;

        // Validate: total_amount cannot be less than paid amount
        if (newTotalAmount < paidAmount) {
          toast.error(
            `Total amount ($${newTotalAmount.toFixed(
              2
            )}) cannot be less than paid amount ($${paidAmount.toFixed(2)})`
          );
          setValue(originalValue);
          setError('Total amount cannot be less than paid amount');
          setTimeout(() => setError(null), 5000);
          return;
        }

        calculatedValues = {
          quantity,
          cash_price: newCashPrice,
          total_amount: newTotalAmount,
          amount_due: amountDue,
        };

        // Note: total_amount is NOT sent to backend
        dataToSend = {
          cash_price: newCashPrice,
          amount_due: amountDue.toFixed(2),
        };
      }

      // Optimistically update UI
      setOptimisticValues(calculatedValues);

      try {
        await requestService.updateRequestDrug(product.id, dataToSend);

        // Update local state with validated value
        setValue(validatedValue.toString());
        setOriginalValue(validatedValue.toString());
        setError(null);

        refetch();
      } catch (err) {
        // Revert to original value on error
        setValue(originalValue);
        setOptimisticValues(null);
        setError('Failed to update. Please try again.');

        // Clear error after 5 seconds
        setTimeout(() => setError(null), 5000);
      }
    },
    [product, fieldName, originalValue, refetch]
  );

  const handleFocus = useCallback(() => {
    setIsEditing(true);
    setError(null);
  }, []);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    if (value !== originalValue) {
      saveValue(value);
    }
  }, [value, originalValue, saveValue]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        (e.target as HTMLInputElement).blur();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setValue(originalValue);
        setIsEditing(false);
        (e.target as HTMLInputElement).blur();
      }
    },
    [originalValue]
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  }, []);

  // Get the current value to display (optimistic or actual)
  const getCurrentValue = () => {
    if (optimisticValues) {
      return fieldName === 'quantity'
        ? optimisticValues.quantity?.toString() || '0'
        : fieldName === 'cash_price'
        ? optimisticValues.cash_price?.toString() || '0'
        : fieldName === 'payable_cash_price'
        ? optimisticValues.payable_cash_price?.toString() || '0'
        : optimisticValues.total_amount?.toString() || '0';
    }
    return value;
  };

  return {
    value: getCurrentValue(),
    isEditing,
    handleFocus,
    handleBlur,
    handleKeyDown,
    handleChange,
    error,
    optimisticValues,
  };
};
